import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import {
  type PageIdentifier,
  PAGES,
} from 'hb-react/shared/constants/pageIdentifiers';
import { type Menu } from 'hb-react/types';

import { type PageNames } from './types';

// Helper hook to get the topic id from the context or the parameter.
export const useTopicId = (topicIdParam?: number): number | undefined => {
  const { topic: contextTopic, topicId: contextTopicId } = useTopicContext();

  // @ts-expect-error untyped context value
  return topicIdParam || contextTopicId || contextTopic?.id;
};

// Select the global menu or the menu of the topic if in the context of topic.
export const useGlobalOrTopicMenu = (topicId?: number): Menu | undefined => {
  const { currentNetwork } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  if (topicId) {
    // Keep in sync with app/assets/javascripts/react/shared/apps/Topics/hooks/useTopicMenu.ts
    return queryClient.getQueryData<Menu>(['topic', topicId, 'menu']);
  }

  // Keep in sync with app/assets/javascripts/react/shared/hooks/useFetchAppPages/index.js
  return (
    currentNetwork?.app_pages || queryClient.getQueryData<Menu>(['appPages'])
  );
};

/**
 * Hook that extracts page names from network and user configuration objects.
 * Currently supports JOURNEYS and PORTFOLIOS page identifiers.
 *
 * @param identifiers - Array of page identifiers to extract names for
 * @returns Object mapping identifiers to their configured names (if available)
 */
export const usePageNamesFromNetworkConfiguration = (
  identifiers: Array<PageIdentifier>,
) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const pageNames = useMemo(
    () => ({
      [PAGES.JOURNEYS]:
        currentNetwork?.features?.journeys?.config?.feature_name,
      [PAGES.PORTFOLIOS]: currentUser?.portfolio?.feature_label,
    }),
    [currentNetwork, currentUser],
  );

  return useMemo(
    () =>
      identifiers.reduce<PageNames>((acc, identifier) => {
        if (pageNames[identifier]) {
          acc[identifier] = pageNames[identifier];
        }
        return acc;
      }, {}),
    [identifiers, pageNames],
  );
};
